<!-- ========== SKELETON LOADER (shown while loading) ========== -->
@if (isLoading) {
@if (compactMode) {
<!-- Widget toolbar skeleton: the saved-view picker on the left, the tools on the right -->
<div class="saved-views-row saved-views-row--compact mb-3" role="status" aria-label="Loading saved views">
  <div class="flex items-end gap-2">
    <div class="flex flex-col gap-1">
      <div class="skeleton-pill skeleton-pill--sm"></div>
      <div class="skeleton-pill skeleton-pill--md"></div>
    </div>
    <div class="compact-actions">
      <div class="skeleton-pill skeleton-pill--md"></div>
      <div class="skeleton-pill skeleton-pill--sm"></div>
    </div>
  </div>
</div>
} @else {
@if (enableViews) {
<!-- Skeleton Pills Row -->
<div class="saved-views-row mb-2">
  <div class="flex items-center gap-2 px-2">
    <div class="flex items-center gap-2 flex-1">
      <div class="skeleton-pill"></div>
      <div class="skeleton-pill skeleton-pill--md"></div>
      <div class="skeleton-pill skeleton-pill--lg"></div>
      <div class="skeleton-pill skeleton-pill--sm"></div>
    </div>
    <div class="skeleton-pill skeleton-pill--action"></div>
    <div class="skeleton-pill skeleton-pill--sm"></div>
  </div>
</div>
}
<!-- Skeleton Filter Row -->
<div class="filter-builder-row mb-2">
  <div class="flex items-center gap-2 px-2">
    <div class="skeleton-filter-btn"></div>
  </div>
</div>
}
}

<!-- ========== LOADED CONTENT ========== -->
@if (!isLoading) {
@if (enableViews) {
@if (compactMode) {
<!-- Compact (dashboard-widget) mode: a slim toolbar — which saved view the widget shows, then its
     tools: Filters & columns (changes for this widget only), Export, and View all. The pill strip,
     Add view and per-view management stay on the module's own page. -->
<div class="saved-views-row saved-views-row--compact mb-3">
  <div class="flex items-end gap-2">
    <!-- Error state -->
    @if (hasError) {
    <div class="empty-state flex items-center gap-2 flex-1">
      <mat-icon class="empty-state-icon text-base" svgIcon="c:danger-circle"></mat-icon>
      <span class="empty-state-text">Couldn't load the saved views. </span>
      <button type="button" (click)="refreshData()" class="empty-state-retry">Try again</button>
    </div>
    } @else {
    <!-- Saved-view picker: a searchable dropdown listing every saved view of the module -->
    <div class="view-switcher view-switcher--stacked sv-popup">
      <span class="view-switcher__label" [id]="filterPanelId + '-view-label'">Saved view</span>
      <div class="relative">
        <button type="button" class="view-switcher-trigger" [class.active]="viewDropdownOpen"
          [attr.aria-labelledby]="filterPanelId + '-view-label'" [attr.aria-expanded]="viewDropdownOpen"
          (click)="toggleViewDropdown($event)" matTooltip="Choose which saved view this widget shows" matTooltipPosition="above">
          <span class="view-switcher-trigger__text truncate">{{ getActiveViewName() }}</span>
          <mat-icon class="view-switcher-trigger__caret" [svgIcon]="viewDropdownOpen ? 'c:chevron-up' : 'c:chevron-down'"></mat-icon>
        </button>
        @if (viewDropdownOpen) {
        <div class="view-switcher-dropdown sv-popup">
          <div class="view-switcher-search">
            <mat-icon class="view-switcher-search__icon" svgIcon="c:search"></mat-icon>
            <input type="text" [(ngModel)]="viewSearchText" (ngModelChange)="onViewSearchChange()"
              aria-label="Search saved views" placeholder="Search saved views…" class="view-switcher-search__input" />
          </div>
          <div class="view-switcher-list">
            @if (isLoadingAllViews && filteredViewItems.length === 0) {
            <div class="view-switcher-empty">Loading saved views…</div>
            }
            @for (item of filteredViewItems; track item.view.id) {
            <button type="button" class="view-switcher-option" [class.active]="isActiveView(item.view)"
              (click)="selectViewFromDropdown(item)">
              @if (isActiveView(item.view)) {
              <mat-icon class="view-switcher-option__check" svgIcon="c:check"></mat-icon>
              } @else {
              <span class="view-switcher-option__check-spacer"></span>
              }
              <span class="view-switcher-option__name truncate">{{ item.view.name === 'Default View' ? 'Default' : item.view.name }}</span>
            </button>
            }
            <!-- Loading indicator while hidden views are being fetched -->
            @if (isLoadingAllViews && filteredViewItems.length > 0) {
            <div class="view-switcher-loading-more">
              <mat-spinner diameter="12"></mat-spinner>
              <span>Loading more…</span>
            </div>
            }
            @if (!isLoadingAllViews && filteredViewItems.length === 0) {
            <div class="view-switcher-empty">No saved views match.</div>
            }
          </div>
        </div>
        }
      </div>
    </div>
    <!-- Projected extra (e.g. the work-package Milestone filter) on the same row -->
    @if (toolbarExtra) {
    <ng-container [ngTemplateOutlet]="toolbarExtra"></ng-container>
    }
    <!-- Tools, kept together on the trailing edge; on a narrow widget they wrap as one group onto
         their own line instead of squeezing the buttons. -->
    <div class="compact-actions">
    <!-- The widget has its own copy of the view (made by saving Filters & columns) -->
    @if (dashboardViewBound) {
    <span class="dash-custom-chip"
      matTooltip="This widget uses a dashboard-specific view with its own filters and columns. The original saved view is unchanged."
      matTooltipPosition="above">
      <mat-icon aria-hidden="true" svgIcon="c:fine-tune"></mat-icon>
      <span>Customised</span>
    </span>
    }
    <!-- Filters & columns — opens the editor; saving changes this widget only -->
    @if (availableColumns.length > 0 && activeView) {
    @let editableFilterCount = isSystemView(activeView) ? 0 : activeFilterCount;
    <button type="button" class="widget-tool-btn"
      [class.widget-tool-btn--active]="editableFilterCount > 0"
      [disabled]="isSystemView(activeView)"
      (click)="editView(activeView)"
      [attr.aria-label]="'Filters and columns' + (editableFilterCount > 0 ? ', ' + editableFilterCount + ' filter' + (editableFilterCount === 1 ? '' : 's') + ' on' : '')"
      [matTooltip]="isSystemView(activeView) ? 'Choose a saved view to change its filters and columns' : 'Change filters and columns for this widget only'"
      matTooltipPosition="above">
      <mat-icon aria-hidden="true" svgIcon="c:fine-tune"></mat-icon>
      <span class="widget-tool-btn__label">Filters &amp; columns</span>
      @if (editableFilterCount > 0) {
      <span class="widget-tool-btn__count" aria-hidden="true">{{ editableFilterCount }}</span>
      }
    </button>
    }
    <!-- Export — the same button the list screens (Schedules, Inspections, Actions) use -->
    @if (showExport) {
    <button type="button" class="list-toolbar__icon" (click)="exportData.emit()"
      matTooltip="Download as Excel" matTooltipPosition="above" aria-label="Download as Excel">
      <mat-icon class="c-fa" svgIcon="c:download" aria-hidden="true"></mat-icon>
    </button>
    }
    <!-- View all → every record of this widget in a side panel -->
    @if (totalCount !== null) {
    <button type="button" class="view-all-link" (click)="viewAll.emit()">
      <span>View all{{ totalCount > 0 ? ' ' + (totalCount | number) : '' }}</span>
      <mat-icon aria-hidden="true" svgIcon="c:chevron-right"></mat-icon>
    </button>
    }
    </div>
    }
  </div>
</div>
} @else {
<!-- View Pills Row -->
<div class="saved-views-row mb-2">
  <div class="saved-views-bar flex items-center gap-2">
    <!-- Error State -->
    @if (hasError) {
    <div class="empty-state flex items-center gap-2 flex-1">
      <mat-icon class="empty-state-icon text-base" svgIcon="c:danger-circle"></mat-icon>
      <span class="empty-state-text">Failed to load views. </span>
      <button (click)="refreshData()" class="empty-state-retry">Retry</button>
    </div>
    }
    <!-- Empty State (no views, no error) -->
    @if (!hasError && visibleViews.length === 0) {
    <div class="empty-state flex items-center gap-2 flex-1">
      <mat-icon class="empty-state-icon text-base" svgIcon="c:eye-slash"></mat-icon>
      <span class="empty-state-text">No views available. Create one to get started.</span>
    </div>
    }
    <!-- Scrollable View Pills with Arrows (only when views exist) -->
    @if (!hasError && visibleViews.length > 0) {
    <div class="views-scroll-wrapper flex-1">
      <!-- Left scroll arrow (always visible, disabled at start) -->
      <button class="scroll-arrow scroll-arrow--left" [class.disabled]="!canScrollLeft" [disabled]="!canScrollLeft"
        (click)="scrollViewsLeft()" matTooltip="Scroll left" matTooltipPosition="above">
        <mat-icon svgIcon="c:chevron-left"></mat-icon>
      </button>
      <!-- Scrollable pills container -->
      <div #viewsScrollContainer class="views-scroll-container" (scroll)="onViewsScroll()" cdkDropList
        cdkDropListOrientation="horizontal" (cdkDropListDropped)="onViewDrop($event)">
        <!-- Default View Pill (always first, not draggable, not removable) -->
        <button
          class="view-pill px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
          [ngClass]="{ 'active': isActiveView(defaultView), 'inactive': !isActiveView(defaultView) }"
          (click)="applyView(defaultView)"
          [matTooltip]="defaultView.name + (isActiveView(defaultView) ? ' (Active)' : '')" matTooltipPosition="above">
          <span class="truncate">{{ defaultView.name }}</span>
          @if (isActiveView(defaultView)) {
          <mat-icon class="check-icon text-base" svgIcon="c:check"></mat-icon>
          }
        </button>
        <!-- Archive View Pill (work-order only, not draggable, not removable) -->
        @if (moduleType === 'work-order') {
        <button
          class="view-pill px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
          [ngClass]="{ 'active': isActiveView(archiveView), 'inactive': !isActiveView(archiveView) }"
          (click)="applyView(archiveView)"
          [matTooltip]="'Shows all archived work orders' + (isActiveView(archiveView) ? ' (Active)' : '')"
          matTooltipPosition="above">
          <span class="truncate">{{ archiveView.name }}</span>
          @if (isActiveView(archiveView)) {
          <mat-icon class="check-icon text-base" svgIcon="c:check"></mat-icon>
          }
        </button>
        }
        <!-- Draft View Pill (work-order only, not draggable, not removable) -->
        @if (moduleType === 'work-order') {
        <button
          class="view-pill px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
          [ngClass]="{ 'active': isActiveView(draftView), 'inactive': !isActiveView(draftView) }"
          (click)="applyView(draftView)"
          [matTooltip]="'Shows all draft work orders' + (isActiveView(draftView) ? ' (Active)' : '')"
          matTooltipPosition="above">
          <span class="truncate">{{ draftView.name }}</span>
          @if (isActiveView(draftView)) {
          <mat-icon class="check-icon text-base" svgIcon="c:check"></mat-icon>
          }
        </button>
        }
        <!-- User-defined View Pills -->
        @for (view of visibleViews; track view) {
        @if (!isDefaultView(view)) {
        <div cdkDrag [cdkDragData]="view" class="view-pill-wrapper">
          <button
            class="view-pill px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
            [ngClass]="{ 'active': isActiveView(view), 'inactive': !isActiveView(view) }" (click)="applyView(view)"
            [matTooltip]="view.name + (isActiveView(view) ? ' (Active)' : '')" matTooltipPosition="above">
            <mat-icon cdkDragHandle class="c-icon-slate drag-handle text-sm cursor-grab">drag_indicator</mat-icon>
            <span class="truncate">{{ view.name }}</span>
            <!-- Visibility indicator icon -->
            <mat-icon class="visibility-icon opacity-50"
              style="font-size: 14px; width: 14px; height: 14px; line-height: 14px;"
              [matTooltip]="view.accessLevel === 'private' ? 'Private' : view.accessLevel === 'team' ? 'Team' : 'Everyone'"
              matTooltipPosition="below" [svgIcon]="view.accessLevel === 'private' ? 'c:lock' : view.accessLevel === 'team' ? 'c:users' : 'c:earth'"></mat-icon>
            <!-- Edit icon for active view -->
            @if (isActiveView(view) && canEdit(view)) {
            <mat-icon
              class="edit-icon text-[14px] cursor-pointer hover:text-[var(--color-action-hover)] transition-colors"
              style="width: 14px; height: 14px; line-height: 14px;" (click)="editView(view, $event)"
              matTooltip="Edit view" matTooltipPosition="below" svgIcon="c:edit"></mat-icon>
            }
            <mat-icon class="remove-icon text-sm cursor-pointer" style="width: 14px; height: 14px; line-height: 14px;"
              matTooltip="Close view" matTooltipPosition="below"
              (click)="removeFromVisibleViews(view, $event)" svgIcon="c:x"></mat-icon>
          </button>
        </div>
        }
        }
      </div>
      <!-- Right scroll arrow (always visible, disabled at end) -->
      <button class="scroll-arrow scroll-arrow--right" [class.disabled]="!canScrollRight" [disabled]="!canScrollRight"
        (click)="scrollViewsRight()" matTooltip="Scroll right" matTooltipPosition="above">
        <mat-icon svgIcon="c:chevron-right"></mat-icon>
      </button>
    </div>
    }
    <!-- Separator between views and action buttons -->
    @if (!hasError && visibleViews.length > 0) {
    <div class="views-action-separator"></div>
    }
    <!-- Order saving indicator -->
    @if (isOrderSaving) {
    <div class="flex items-center gap-1 flex-shrink-0 text-xs text-gray-500 px-1">
      <mat-spinner diameter="12"></mat-spinner>
      <span>Saving order…</span>
    </div>
    }
    <!-- Add View Button (sticky, always visible) -->
    <button (click)="openViewsListDialog()"
      class="add-view-btn px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 transition-all border">
      <mat-icon class="text-base" svgIcon="c:plus"></mat-icon>
      <span>Add view</span>
    </button>
    <!-- All Views Button (hidden in compact/widget mode) -->
    @if (!compactMode) {
    <button (click)="navigateToAllViews()"
      class="all-views-btn px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 transition-all">
      <mat-icon class="text-base" svgIcon="c:list"></mat-icon>
      <span>All views</span>
    </button>
    }
    <!-- Compact mode: Filters toggle pill (only shown in widget/compact mode) -->
    @if (compactMode && availableColumns.length > 0) {
    <button (click)="toggleCompactFilter()"
      class="compact-filter-pill px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 transition-all border"
      [class.compact-filter-pill--active]="compactFilterExpanded || activeFilterCount > 0"
      [matTooltip]="compactFilterExpanded ? 'Hide filters' : 'Show filters'" matTooltipPosition="above">
      <mat-icon class="text-base" svgIcon="c:fine-tune"></mat-icon>
      <span>Filters</span>
      @if (activeFilterCount > 0) {
        <span class="compact-filter-dot" aria-label="Filter applied"></span>
      }
    </button>
    }
    <!-- Grid tools (search, Edit Columns, the host's own actions) at the end of the views row, so the
         filter row below has its whole width for filters instead of losing a line to the search. -->
    @if (gridToolsInViewsRow) {
    <div class="views-row-tools ml-auto flex items-center gap-2 flex-shrink-0">
      <ng-container [ngTemplateOutlet]="gridTools"></ng-container>
    </div>
    }
  </div>
</div>
}
}
<!-- Filter Builder Row. Full page: a one-line filter bar — the Filters toggle with what is filtering
     the list, and the view's own actions — over a panel holding the filter editor and the host's
     quick filters, opened from the bar. Widget/compact: the drawer the Filters pill opens, unchanged. -->
@if (availableColumns.length > 0 && (!compactMode || compactFilterExpanded)) {
<div class="filter-builder-row" [class.mb-3]="compactMode" [class.mb-2]="!compactMode" [class.compact-filter-drawer]="compactMode">
  <!-- Drawer header (compact/widget mode only) -->
  @if (compactMode) {
  <div class="compact-filter-drawer__header">
    <div class="compact-filter-drawer__title">
      <mat-icon svgIcon="c:fine-tune"></mat-icon>
      <span>Filters</span>
      @if (activeFilterCount > 0) {
        <span class="compact-filter-drawer__count">{{ activeFilterCount }} active</span>
      }
    </div>
    <button type="button" class="compact-filter-drawer__close" (click)="toggleCompactFilter()"
      matTooltip="Hide filters" aria-label="Hide filters">
      <mat-icon svgIcon="c:x"></mat-icon>
    </button>
  </div>
  }
  @if (!compactMode) {
  @let summary = filterSummary;
  <div class="sv-filter-bar">
    <!-- One control: the Filters pill and, while folded, what is filtering the list. Any of it opens
         or folds the panel, and a screen reader hears it all as the button's name. -->
    <button type="button" class="sv-filter-toggle" [class.sv-filter-toggle--open]="filtersExpanded"
      [class.sv-filter-toggle--on]="summary.count > 0" [attr.aria-expanded]="filtersExpanded"
      [attr.aria-controls]="filterPanelId" (click)="toggleFiltersExpanded()">
      <span class="sv-filter-toggle__pill">
        <mat-icon aria-hidden="true" svgIcon="c:fine-tune"></mat-icon>
        <span>Filters</span>
        @if (summary.count > 0) {
        <span class="sv-filter-toggle__count">{{ summary.count }}</span>
        }
        <mat-icon class="sv-filter-toggle__caret" aria-hidden="true" svgIcon="c:chevron-down"></mat-icon>
      </span>
      @if (!filtersExpanded) {
      @for (item of summary.shown; track $index) {
      <span class="sv-summary-chip" [class.sv-summary-chip--quick]="!item.label"
        [title]="item.label ? item.label + ' ' + item.text : item.text">
        @if (item.label) {
        <span class="sv-summary-chip__label">{{ item.label }}</span>{{ ' ' }}
        }
        <span class="sv-summary-chip__text">{{ item.text }}</span>
      </span>
      }
      @if (summary.more > 0) {
      <span class="sv-summary-more">+{{ summary.more }} more</span>
      }
      @if (summary.count === 0) {
      <span class="sv-summary-none">No filters applied</span>
      }
      }
    </button>
    @if (isActiveView(defaultView) && hasAppliedFilters) {
    <mat-icon class="default-view-warning-icon flex-shrink-0" aria-label="Default view filters are saved on this device only"
      matTooltip="Filters in the default view are saved locally and will persist on refresh. To share filters with others, create a custom view or select an existing view."
      matTooltipPosition="above" matTooltipClass="default-view-warning-tooltip" svgIcon="c:info-circle"></mat-icon>
    }
    <!-- The view's actions, and — for hosts without a views row (the EOI register, Contacts) — the
         grid tools. Always on the bar, folded or not. -->
    <div class="sv-filter-bar__end">
      @if (activeView && !isSystemView(activeView)) {
      <div class="view-inline-actions ml-auto flex items-center gap-1 flex-shrink-0 pr-2">
        <!-- Edit Columns / Clone View are full-page actions, hidden in widget mode -->
        <button type="button" class="view-action-icon-btn view-action-icon-btn--labeled" (click)="openEditColumnsDialog()"
          matTooltip="Edit column visibility" matTooltipPosition="above">
          <mat-icon svgIcon="c:columns"></mat-icon>
          <span>Edit Columns</span>
        </button>
        <button type="button" class="view-action-icon-btn view-action-icon-btn--labeled" (click)="openCloneViewDialog()"
          matTooltip="Clone this view" matTooltipPosition="above">
          <mat-icon svgIcon="c:copy"></mat-icon>
          <span>Clone View</span>
        </button>
        <div class="view-action-vsep"></div>
        <button type="button" class="view-action-icon-btn" [disabled]="!isDirty" (click)="revertView()"
          matTooltip="Revert unsaved changes" matTooltipPosition="above">
          <mat-icon svgIcon="c:undo"></mat-icon>
        </button>
        <button type="button" class="view-action-icon-btn view-action-icon-btn--save"
          [disabled]="!isDirty || isSavingView" (click)="saveCurrentView()" matTooltip="Save filter changes to this view"
          matTooltipPosition="above">
          @if (isSavingView) {
          <mat-spinner diameter="14"></mat-spinner>
          } @else {
          <mat-icon svgIcon="c:save"></mat-icon>
          }
        </button>
      </div>
      }
      @if (gridToolsInFilterRow) {
      <div class="filter-row-tools flex items-center gap-2 flex-shrink-0">
        <ng-container [ngTemplateOutlet]="gridTools"></ng-container>
      </div>
      }
    </div>
  </div>
  }
  <!-- The editor. Full page: hidden (not removed) while folded, so every chip, popup and pending
       value keeps its state exactly as when it was always on screen. -->
  <div [id]="filterPanelId" class="sv-filter-panel" [class.sv-filter-panel--page]="!compactMode"
    [hidden]="!compactMode && !filtersExpanded">
  <div class="flex items-center gap-2 px-2 flex-wrap" [class.filter-row--split]="compactMode && gridToolsInFilterRow">
    <!--
      The filter controls, grouped so they can wrap within their own column while the grid tools stay
      pinned top-right. Transparent to layout (`display: contents`) unless the row is split, so a row
      without grid tools lays out exactly as it did before this wrapper existed.
    -->
    <div class="filter-row__filters">
    <!-- Warning icon: shown only when default view is active AND filters are applied (widget mode;
         the full page shows it on the filter bar) -->
    @if (compactMode && isActiveView(defaultView) && hasAppliedFilters) {
    <mat-icon class="default-view-warning-icon flex-shrink-0" aria-label="Default view filters are saved on this device only"
      matTooltip="Filters in the default view are saved locally and will persist on refresh. To share filters with others, create a custom view or select an existing view."
      matTooltipPosition="above" matTooltipClass="default-view-warning-tooltip" svgIcon="c:info-circle"></mat-icon>
    }
    <!-- Applied Filters -->
    <!-- By position, not identity: every row is driven by (filter, i) and its handlers take the index,
         while relinkAppliedFilterColumns() and a view switch hand back fresh filter objects (NG0956). -->
    @for (filter of appliedFilters; track $index; let i = $index) {
    <!--
          Compute all display flags once per filter per change-detection cycle.
          This avoids calling each method 4-6 times per filter in the template below.
          *ngIf on a plain object is always truthy, so the content always renders.
          -->
    @if ({
    isUser: isUserField(filter.column) && userOptions.length > 0,
    isLookup: isLookupField(filter.column),
    isDropdown: isDropdownColumn(filter.column) || isStateField(filter.column),
    isState: isStateField(filter.column),
    isTag: isTagsField(filter.column),
    isMilestone: isMilestoneColumn(filter.column),
    isBetween: isBetween(filter.operator),
    isMulti: isMulti(filter.operator),
    isNoValue: isNoValue(filter.operator)
    }; as c) {
    <!-- How the filters combine, said where it applies: between them. The first joiner is the
         control (and / or — the same picker as a chip's operator); the rest echo its choice. -->
    @if (i === 1) {
    <select class="filter-logic-select flex-shrink-0" [ngModel]="filterLogic" (ngModelChange)="setFilterLogic($event)"
      aria-label="Combine filters with" title="and: every filter must match · or: any filter can match">
      <option value="AND">and</option>
      <option value="OR">or</option>
    </select>
    } @else if (i > 1) {
    <span class="filter-logic-separator text-xs font-semibold text-gray-400 flex-shrink-0 select-none px-0.5">
      {{ filterLogic }}
    </span>
    }
    <div class="filter-chip flex items-center gap-2 px-2.5 py-1.5 rounded text-xs">
      <!-- Column Name -->
      <span class="font-medium text-gray-700">{{ filter.column.headerText }}</span>
      <!-- Milestone Selector: shared across all milestone filters — changing any updates all -->
      @if (shouldShowMilestoneSelector(filter.column) && milestoneOptions.length > 0) {
      <select [ngModel]="sharedMilestoneId" (ngModelChange)="updateSharedMilestoneId($event)"
        class="filter-select text-xs cursor-pointer min-w-[120px]">
        <option [ngValue]="0">Any milestone...</option>
        @for (ms of milestoneOptions; track ms) {
        <option [ngValue]="ms.id">{{ ms.name }}</option>
        }
      </select>
      }
      <!-- Operator Dropdown -->
      <select [ngModel]="filter.operator" (change)="updateFilterOperator(i, $any($event.target).value)"
        [attr.aria-label]="filter.column.headerText + ' condition'"
        class="filter-select text-xs cursor-pointer">
        @for (op of getOperatorsForColumn(filter.column); track op) {
        <option [value]="op.value">{{ op.label }}</option>
        }
      </select>
      <!-- ===== Between operator: two range inputs ===== -->
      @if (c.isBetween && !c.isNoValue) {
      <input [type]="getInputType(filter.column)" [attr.step]="isNumberField(filter.column) ? 'any' : null"
        [value]="filter.value" (input)="updateFilterValue(i, $any($event.target).value)"
        [placeholder]="getPlaceholder(filter.column, 'start')" class="filter-input text-xs rounded px-2 py-1 w-24" />
      <span class="text-gray-500 text-xs">to</span>
      <input [type]="getInputType(filter.column)" [attr.step]="isNumberField(filter.column) ? 'any' : null"
        [value]="filter.valueTo" (input)="updateFilterValueTo(i, $any($event.target).value)"
        [placeholder]="getPlaceholder(filter.column, 'end')" class="filter-input text-xs rounded px-2 py-1 w-24" />
      }
      <!-- ===== Multi-value operator (in / notin) ===== -->
      @if (!c.isBetween && c.isMulti && !c.isNoValue) {
      <!-- User field: searchable multi-select -->
      @if (c.isUser) {
      <div class="relative inline-block">
        <button type="button"
          class="filter-select text-xs cursor-pointer min-w-[150px] flex items-center justify-between border border-gray-300 rounded px-2 py-1.5 bg-white hover:bg-gray-50 transition-colors"
          (click)="toggleUserFilterDropdown(i, $event)">
          @if (!filter.multiValues || filter.multiValues.length === 0) {
          <span class="filter-value-placeholder">Select users…</span>
          } @else {
          <span class="filter-value-summary" [title]="multiValueSummary(filter, 'user', true)">{{ multiValueSummary(filter, 'user') }}</span>
          }
          <mat-icon class="text-gray-400" svgIcon="c:chevron-down"></mat-icon>
        </button>
        @if (activeUserFilterIndex === i) {
        <div
          class="sv-popup absolute bg-white rounded-lg border border-gray-200 z-50 min-w-[240px] max-h-[300px] shadow-lg flex flex-col overflow-hidden"
          [ngClass]="{
                         'top-full': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'mt-1': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'bottom-full': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'mb-1': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'left-0': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'top-left',
                         'right-0': userFilterDropdownPosition === 'bottom-right' || userFilterDropdownPosition === 'top-right'
                       }">
          <div class="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
            <input type="text" [(ngModel)]="userFilterSearchTerm" placeholder="Search users..."
              class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-slate-500 focus:ring-1 placeholder-gray-400"
              (click)="$event.stopPropagation()">
          </div>
          @if (getFilteredUsers().length > 0) {
          <cdk-virtual-scroll-viewport class="sv-option-viewport" [itemSize]="optionRowHeight"
            [style.height.px]="optionListHeight(getFilteredUsers().length)">
            <label *cdkVirtualFor="let user of getFilteredUsers(); trackBy: trackByOptionId"
              class="sv-option-row px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-xs transition-colors">
              <input type="checkbox" [checked]="isUserSelected(filter, user.id)"
                (change)="toggleUserMultiSelection(i, user.id, $event)"
                class="rounded border-gray-300 text-primary accent-[var(--color-action)] h-3.5 w-3.5">
              <span class="truncate text-gray-700" [title]="user.name">{{ user.name }}</span>
            </label>
          </cdk-virtual-scroll-viewport>
          } @else {
          <div class="p-4 text-center text-gray-400 text-xs italic">No users found</div>
          }
        </div>
        }
      </div>
      }
      <!-- Lookup field (Status, Type): show names, store IDs -->
      @if (!c.isUser && c.isLookup) {
      <div class="relative inline-block">
        <button type="button"
          class="filter-select text-xs cursor-pointer min-w-[150px] flex items-center justify-between border border-gray-300 rounded px-2 py-1.5 bg-white hover:bg-gray-50 transition-colors"
          (click)="toggleUserFilterDropdown(i, $event)">
          @if (!filter.multiValues || filter.multiValues.length === 0) {
          <span class="filter-value-placeholder">Select…</span>
          } @else {
          <span class="filter-value-summary" [title]="multiValueSummary(filter, 'lookup', true)">{{ multiValueSummary(filter, 'lookup') }}</span>
          }
          <mat-icon class="text-sm text-gray-400" svgIcon="c:chevron-down"></mat-icon>
        </button>
        @if (activeUserFilterIndex === i) {
        <div
          class="sv-popup absolute bg-white rounded-lg border border-gray-200 z-50 min-w-[240px] max-h-[250px] shadow-lg flex flex-col overflow-hidden"
          [ngClass]="{
                         'top-full': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'mt-1': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'bottom-full': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'mb-1': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'left-0': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'top-left',
                         'right-0': userFilterDropdownPosition === 'bottom-right' || userFilterDropdownPosition === 'top-right'
                       }">
          <div class="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
            <input type="text" [(ngModel)]="userFilterSearchTerm" placeholder="Search..."
              class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-slate-500 focus:ring-1 placeholder-gray-400"
              (click)="$event.stopPropagation()">
          </div>
          @if (getFilteredLookupOptions(filter.column).length > 0) {
          <cdk-virtual-scroll-viewport class="sv-option-viewport" [itemSize]="optionRowHeight"
            [style.height.px]="optionListHeight(getFilteredLookupOptions(filter.column).length)">
            <label *cdkVirtualFor="let option of getFilteredLookupOptions(filter.column); trackBy: trackByOptionId"
              class="sv-option-row px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-xs transition-colors">
              <input type="checkbox" [checked]="isUserSelected(filter, option.id)"
                (change)="toggleUserMultiSelection(i, option.id, $event)"
                class="rounded border-gray-300 text-primary accent-[var(--color-action)] h-3.5 w-3.5">
              <span class="truncate text-gray-700" [title]="option.name">{{ option.name }}</span>
            </label>
          </cdk-virtual-scroll-viewport>
          } @else {
          <div class="p-4 text-center text-gray-400 text-xs italic">No options found</div>
          }
        </div>
        }
      </div>
      }
      <!-- Regular dropdown / state column: multi-select panel -->
      @if (!c.isUser && !c.isLookup && c.isDropdown) {
      <div class="relative inline-block">
        <button type="button"
          class="filter-select text-xs cursor-pointer min-w-[150px] flex items-center justify-between border border-gray-300 rounded px-2 py-1.5 bg-white hover:bg-gray-50 transition-colors"
          (click)="toggleUserFilterDropdown(i, $event)">
          @if (!filter.multiValues || filter.multiValues.length === 0) {
          <span class="filter-value-placeholder">Select…</span>
          } @else {
          <span class="filter-value-summary" [title]="multiValueSummary(filter, 'plain', true)">{{ multiValueSummary(filter, 'plain') }}</span>
          }
          <mat-icon class="text-gray-400" svgIcon="c:chevron-down"></mat-icon>
        </button>
        @if (activeUserFilterIndex === i) {
        <div
          class="sv-popup absolute bg-white rounded-lg border border-gray-200 z-50 min-w-[240px] max-h-[250px] shadow-lg flex flex-col overflow-hidden"
          [ngClass]="{
                         'top-full': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'mt-1': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'bottom-full': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'mb-1': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'left-0': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'top-left',
                         'right-0': userFilterDropdownPosition === 'bottom-right' || userFilterDropdownPosition === 'top-right'
                       }">
          <div class="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
            <input type="text" [(ngModel)]="userFilterSearchTerm" placeholder="Search..."
              class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-slate-500 focus:ring-1 placeholder-gray-400"
              (click)="$event.stopPropagation()">
          </div>
          @if (getFilteredDropdownOptions(filter.column, c.isState).length > 0) {
          <cdk-virtual-scroll-viewport class="sv-option-viewport" [itemSize]="optionRowHeight"
            [style.height.px]="optionListHeight(getFilteredDropdownOptions(filter.column, c.isState).length)">
            <label *cdkVirtualFor="let option of getFilteredDropdownOptions(filter.column, c.isState); trackBy: trackByOptionValue"
              class="sv-option-row px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-xs transition-colors">
              <input type="checkbox" [checked]="isUserSelected(filter, option)"
                (change)="toggleUserMultiSelection(i, option, $event)"
                class="rounded border-gray-300 text-primary accent-[var(--color-action)] h-3.5 w-3.5">
              <span class="truncate text-gray-700" [title]="option">{{ option }}</span>
            </label>
          </cdk-virtual-scroll-viewport>
          } @else {
          <div class="p-4 text-center text-gray-400 text-xs italic">No options found</div>
          }
        </div>
        }
      </div>
      }
      <!-- Tags field: searchable multi-select from API -->
      @if (!c.isUser && !c.isLookup && !c.isDropdown && c.isTag) {
      <div class="relative inline-block">
        <button type="button"
          class="filter-select text-xs cursor-pointer min-w-[150px] flex items-center justify-between border border-gray-300 rounded px-2 py-1.5 bg-white hover:bg-gray-50 transition-colors"
          (click)="toggleUserFilterDropdown(i, $event)">
          @if (!filter.multiValues || filter.multiValues.length === 0) {
          <span class="filter-value-placeholder">Select tags…</span>
          } @else {
          <span class="filter-value-summary" [title]="multiValueSummary(filter, 'plain', true)">{{ multiValueSummary(filter, 'plain') }}</span>
          }
          <mat-icon class="text-sm text-gray-400" svgIcon="c:chevron-down"></mat-icon>
        </button>
        @if (activeUserFilterIndex === i) {
        <div
          class="sv-popup absolute bg-white rounded-lg border border-gray-200 z-50 min-w-[240px] max-h-[250px] shadow-lg flex flex-col overflow-hidden"
          [ngClass]="{
                         'top-full': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'mt-1': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'bottom-full': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'mb-1': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'left-0': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'top-left',
                         'right-0': userFilterDropdownPosition === 'bottom-right' || userFilterDropdownPosition === 'top-right'
                       }">
          <div class="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
            <input type="text" [(ngModel)]="userFilterSearchTerm" placeholder="Search tags..."
              class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-slate-500 focus:ring-1 placeholder-gray-400"
              (click)="$event.stopPropagation()">
          </div>
          @if (tagsLoading) {
          <div class="p-4 text-center text-gray-400 text-xs italic">Loading...</div>
          } @else if (getFilteredTagOptions().length > 0) {
          <cdk-virtual-scroll-viewport class="sv-option-viewport" [itemSize]="optionRowHeight"
            [style.height.px]="optionListHeight(getFilteredTagOptions().length)">
            <label *cdkVirtualFor="let tag of getFilteredTagOptions(); trackBy: trackByOptionId"
              class="sv-option-row px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-xs transition-colors">
              <input type="checkbox" [checked]="isUserSelected(filter, tag.name)"
                (change)="toggleUserMultiSelection(i, tag.name, $event)"
                class="rounded border-gray-300 text-primary accent-[var(--color-action)] h-3.5 w-3.5">
              <span class="truncate text-gray-700" [title]="tag.name">{{ tag.name }}</span>
            </label>
          </cdk-virtual-scroll-viewport>
          } @else {
          <div class="p-4 text-center text-gray-400 text-xs italic">No tags found</div>
          }
        </div>
        }
      </div>
      }
      <!-- Non-dropdown: tag chip input -->
      @if (!c.isUser && !c.isLookup && !c.isDropdown && !c.isTag) {
      <div
        class="filter-multi-input inline-flex items-center flex-wrap gap-1 border border-gray-200 rounded bg-white px-2 py-1 min-w-[140px] cursor-text">
        @for (val of filter.multiValues; track val; let vi = $index) {
        <span
          class="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-xs border border-gray-200 leading-none">
          {{ val }}
          <button type="button" (click)="removeMultiValue(i, vi)"
            class="inline-flex items-center justify-center w-3 h-3 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
            <mat-icon style="font-size: 11px; width: 11px; height: 11px; line-height: 11px;" svgIcon="c:x"></mat-icon>
          </button>
        </span>
        }
        <input [type]="getInputType(filter.column)" [attr.step]="isNumberField(filter.column) ? 'any' : null"
          [placeholder]="(filter.multiValues?.length || 0) > 0 ? 'Add more...' : (isNumberField(filter.column) ? 'e.g. 101, Enter to add' : 'Type, Enter to add')"
          (keydown.enter)="addMultiValue(i, $any($event.target).value, $event)"
          class="text-xs bg-transparent border-none outline-none min-w-[80px] flex-1 py-0 placeholder-gray-400" />
      </div>
      }
      }
      <!-- ===== Default: single value input or dropdown ===== -->
      @if (!c.isBetween && !c.isMulti && !c.isNoValue) {
      <!-- User field: searchable single-select -->
      @if (c.isUser) {
      <div class="relative inline-block">
        <button type="button"
          class="filter-select text-xs cursor-pointer min-w-[150px] flex items-center justify-between border border-gray-300 rounded px-2 py-1.5 !bg-white hover:bg-gray-50 transition-colors"
          (click)="toggleUserFilterDropdown(i, $event)">
          @if (filter.value) {
          <span class="text-gray-700 truncate block max-w-[120px]">{{ getUserNameById(filter.value) }}</span>
          }
          @if (!filter.value) {
          <span class="text-gray-500 italic">Select User...</span>
          }
          <mat-icon class="text-gray-400" svgIcon="c:chevron-down"></mat-icon>
        </button>
        @if (activeUserFilterIndex === i) {
        <div
          class="sv-popup absolute bg-white rounded-lg border border-gray-200 z-50 min-w-[240px] max-h-[300px] shadow-lg flex flex-col overflow-hidden"
          [ngClass]="{
                         'top-full': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'mt-1': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'bottom-right',
                         'bottom-full': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'mb-1': userFilterDropdownPosition === 'top-left' || userFilterDropdownPosition === 'top-right',
                         'left-0': userFilterDropdownPosition === 'bottom-left' || userFilterDropdownPosition === 'top-left',
                         'right-0': userFilterDropdownPosition === 'bottom-right' || userFilterDropdownPosition === 'top-right'
                       }">
          <div class="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
            <input type="text" [(ngModel)]="userFilterSearchTerm" placeholder="Search user..."
              class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-slate-500 focus:ring-1 placeholder-gray-400"
              (click)="$event.stopPropagation()">
          </div>
          <div class="flex flex-col min-h-0" role="listbox" [attr.aria-label]="filter.column.headerText + ' value'">
            @if (!userFilterSearchTerm) {
            <div role="option" tabindex="0" [attr.aria-selected]="!filter.value"
              (click)="selectUserFilter(i, '', $event)"
              (keydown.enter)="selectUserFilter(i, '', $event)"
              (keydown.space)="selectUserFilter(i, '', $event)"
              class="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-xs text-gray-500 italic border-b border-gray-50">
              <span>Clear selection</span>
            </div>
            }
            @if (getFilteredUsers().length > 0) {
            <cdk-virtual-scroll-viewport class="sv-option-viewport" [itemSize]="optionRowHeight"
              [style.height.px]="optionListHeight(getFilteredUsers().length)">
              <div *cdkVirtualFor="let user of getFilteredUsers(); trackBy: trackByOptionId"
                role="option" tabindex="0" [attr.aria-selected]="filter.value === user.id"
                (click)="selectUserFilter(i, user.id, $event)"
                (keydown.enter)="selectUserFilter(i, user.id, $event)"
                (keydown.space)="selectUserFilter(i, user.id, $event)"
                class="sv-option-row px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-xs transition-colors"
                [class.bg-[var(--color-selected-bg)]]="filter.value === user.id">
                <span class="truncate text-gray-700" [title]="user.name" [class.font-medium]="filter.value === user.id">{{ user.name }}</span>
                @if (filter.value === user.id) {
                <mat-icon class="text-primary text-sm ml-auto" svgIcon="c:check"></mat-icon>
                }
              </div>
            </cdk-virtual-scroll-viewport>
            } @else {
            <div class="p-4 text-center text-gray-400 text-xs italic">No users found</div>
            }
          </div>
        </div>
        }
      </div>
      }
      <!-- Lookup field (Status, Type): names displayed, IDs stored -->
      @if (!c.isUser && c.isLookup) {
      <select [ngModel]="filter.value" (change)="updateFilterValue(i, $any($event.target).value)"
        [attr.aria-label]="filter.column.headerText + ' value'"
        class="filter-select text-xs cursor-pointer min-w-[120px]">
        <option value="">Select...</option>
        @for (option of filter.column.lookupOptions; track option) {
        <option [value]="option.id">{{ option.name }}</option>
        }
      </select>
      }
      <!-- Regular dropdown / state single select -->
      @if (!c.isUser && !c.isLookup && c.isDropdown) {
      <select [ngModel]="filter.value" (change)="updateFilterValue(i, $any($event.target).value)"
        [attr.aria-label]="filter.column.headerText + ' value'"
        class="filter-select text-xs cursor-pointer min-w-[120px]">
        <option value="">Select...</option>
        @for (option of (c.isState ? stateOptions : filter.column.options); track option) {
        <option [value]="option">{{ option }}</option>
        }
      </select>
      }
      <!-- Text / number / date input -->
      @if (!c.isUser && !c.isLookup && !c.isDropdown && !c.isTag) {
      <input [type]="getInputType(filter.column)" [attr.step]="isNumberField(filter.column) ? 'any' : null"
        [value]="filter.value" (input)="updateFilterValue(i, $any($event.target).value)"
        [placeholder]="getPlaceholder(filter.column)" class="filter-input text-xs rounded px-2 py-1 w-32" />
      }
      }
      <!-- Remove Button -->
      <button (click)="removeFilter(i)" [attr.aria-label]="'Remove ' + filter.column.headerText + ' filter'"
        class="remove-filter-btn flex items-center justify-center w-3.5 h-3.5 text-gray-400 hover:text-red-500 transition-colors ml-0.5">
        <mat-icon class="text-xs" svgIcon="c:x"></mat-icon>
      </button>
    </div>
    }
    }
    <!-- A column's options are still being fetched, so the column list is not yet the real one.
         A skeleton in the button's place rather than a button that opens a short list. -->
    @if (filterOptionsLoading) {
    <div class="skeleton-filter-btn" role="status" aria-live="polite" aria-label="Loading filters"></div>
    } @else {
    <!-- Filter / Add Filter Button with Dropdown -->
    <div class="relative">
      <button (click)="toggleFilterMenu($event)" [disabled]="getAvailableColumnsForFilter().length === 0"
        class="filter-toggle-btn px-2.5 py-1.5 rounded text-xs font-medium transition-all flex items-center gap-1 disabled:cursor-not-allowed">
        <!-- The full page's bar already says "Filters", so its panel only ever adds one. -->
        <mat-icon class="text-xs" [svgIcon]="hasAppliedFilters || !compactMode ? 'c:plus' : 'c:fine-tune'"></mat-icon>
        <span>{{ hasAppliedFilters || !compactMode ? 'Add filter' : 'Filter' }}</span>
      </button>
      <!-- Dropdown Menu -->
      @if (showFilterMenu) {
      <div
        class="filter-menu sv-popup absolute bg-white rounded-lg border border-gray-200 z-50 min-w-[200px] max-h-[400px] overflow-y-auto"
        [ngClass]="{
            'top-full': filterMenuPosition === 'bottom-left' || filterMenuPosition === 'bottom-right',
            'mt-1': filterMenuPosition === 'bottom-left' || filterMenuPosition === 'bottom-right',
            'bottom-full': filterMenuPosition === 'top-left' || filterMenuPosition === 'top-right',
            'mb-1': filterMenuPosition === 'top-left' || filterMenuPosition === 'top-right',
            'left-0': filterMenuPosition === 'bottom-left' || filterMenuPosition === 'top-left',
            'right-0': filterMenuPosition === 'bottom-right' || filterMenuPosition === 'top-right'
          }">
        <div class="sticky top-0 bg-white border-b border-gray-200 px-2 py-2">
          <input type="text" [(ngModel)]="filterSearchTerm" placeholder="Search columns..."
            class="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-slate-500"
            (keydown.escape)="closeFilterMenu()" />
        </div>
        @for (column of getFilteredColumnsForFilter(); track column) {
        <button (click)="addFilter(column)"
          class="filter-menu-item w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2">
          <mat-icon class="text-sm text-gray-400 flex-shrink-0"
            [matTooltip]="column.columnType === 'milestone' ? 'Milestone field' : column.columnType === 'custom' ? 'Custom field' : 'Standard field'"
            matTooltipPosition="right" [svgIcon]="column.columnType === 'milestone' ? 'c:flag' : column.columnType === 'custom' ? 'c:fine-tune' : 'c:columns'"></mat-icon>
          <span>{{ column.headerText }}</span>
        </button>
        }
        @if (getFilteredColumnsForFilter().length === 0) {
        <div class="px-4 py-2 text-xs text-gray-500 text-center">
          {{ getAvailableColumnsForFilter().length === 0 ? 'All columns are already filtered' : 'No columns found' }}
        </div>
        }
      </div>
      }
    </div>
    }
    <!-- Clear All Button -->
    @if (hasAppliedFilters) {
    <button (click)="clearAllFilters()"
      class="clear-all-btn px-2.5 py-1.5 rounded text-xs font-medium text-gray-600 transition-all">
      Clear all
    </button>
    }
    </div>
    <!-- Widget mode keeps its grid tools on the drawer's filter row; the full page carries them on
         the filter bar above. -->
    @if (compactMode && gridToolsInFilterRow) {
    <div class="filter-row-tools ml-auto flex items-center gap-2 flex-shrink-0">
      <ng-container [ngTemplateOutlet]="gridTools"></ng-container>
    </div>
    }
  </div>
  <!-- The host's quick filters (Schedules, Bookings), edited alongside the filters. -->
  @if (!compactMode) {
  <div class="sv-quick-filters-slot">
    <ng-content select="[svQuickFilters]"></ng-content>
  </div>
  }
  </div>
</div>
}
}
<!-- Search, Edit Columns and the host's own grid actions — rendered in exactly one place:
     the views row when there is one, otherwise the filter row. -->
<ng-template #gridTools>
  @if (searchControl) {
  <mat-form-field appearance="outline" class="cmn-sm-form-field hide-hint common-searchbar filter-row-search"
    subscriptSizing="dynamic">
    <mat-icon matPrefix svgIcon="c:search"></mat-icon>
    <input type="text" matInput [placeholder]="searchPlaceholder" [formControl]="searchControl" />
    @if (!!searchControl.value) {
    <mat-icon matSuffix class="!p-0 !pr-1 !pb-1 text-lg cursor-pointer"
      (click)="searchControl.reset()" svgIcon="c:x"></mat-icon>
    }
  </mat-form-field>
  }
  @if (columnConfig.length > 0) {
  <app-column-chooser-button [columnConfig]="columnConfig" [hiddenColumns]="hiddenColumns"
    (hiddenColumnsChange)="hiddenColumnsChange.emit($event)">
  </app-column-chooser-button>
  }
  <!-- The host's own grid actions, set apart by a divider: what you see (search, columns) on one
       side, what you do with it on the other. -->
  @if (gridToolsExtra) {
  <div class="grid-tools-vsep"></div>
  <ng-container [ngTemplateOutlet]="gridToolsExtra"></ng-container>
  }
</ng-template>
@if (isApplyingView) {
<mat-progress-bar mode="indeterminate"></mat-progress-bar>
}